import React, { useEffect, useState } from 'react'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'

interface SaveAsTemplateDialogProps {
  open: boolean
  /** Pre-fills the name field — the source dashboard's own name. */
  defaultName: string
  onClose: () => void
  onSave: (name: string, tags: string[]) => void
  isLoading?: boolean
}

/**
 * "Save as template" (#971 Phase 2) — prompts for a template name and
 * optional tags before capturing structure + widget config shells only. The
 * actual stripping happens server-side (dashboardcards.TemplateFromDashboard)
 * so this dialog only collects the two things the user chooses.
 */
const SaveAsTemplateDialog: React.FC<SaveAsTemplateDialogProps> = ({ open, defaultName, onClose, onSave, isLoading }) => {
  const [name, setName] = useState(defaultName)
  const [tagsInput, setTagsInput] = useState('')

  // Re-seed the name whenever a new dashboard's dialog opens — a plain
  // render-phase guard would fire on every keystroke since `open` doesn't
  // change; the dialog's identity does change per-target, so this is a
  // one-shot reset tied to the `open` transition, not derived state.
  useEffect(() => {
    if (open) {
      setName(defaultName)
      setTagsInput('')
    }
  }, [open, defaultName])

  const handleSave = () => {
    const trimmed = name.trim()
    if (!trimmed) return
    const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean)
    onSave(trimmed, tags)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Save as template</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Captures the layout and widget shells only — no data, table selections, or generated text.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          label="Template name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave() }}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Tags (optional)"
          placeholder="exec report, chart-led"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave() }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Comma-separated
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="info"
          onClick={handleSave}
          disabled={!name.trim() || isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Save template
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SaveAsTemplateDialog
